/* eslint-disable @typescript-eslint/naming-convention */
import * as am4core from '@amcharts/amcharts4/core';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';

import {
    AxisRendererX, AxisRendererY, CircleBullet, Cursor, DateAxis, Legend, LineSeries, Series, ValueAxis, XYChart, XYCursor
} from '@amcharts/amcharts4/charts';
import { timeScalesEnum } from '../../../../Monitor/Shared/enums/timeScales.enum';
import { SeriesModel } from '../series.model';
import { ChartMetadataModel } from '../chart-metadata.model';
import { ChartModel } from '../chart.model';
import { DateAndValueXYChartOptionsModel } from '../date-and-value-x-y-chart-options.model';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { CustomLegendItem } from '../custom-legend-item.model';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { ChartSizeHelper } from '../../helpers/chart-size.helper';
import { ChartTimeIntervalsEnum } from '../../../../Common/Reports/enums/chart-time-intervals.enum';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';


export class StackedRiverChartModel extends ChartModel {

    public declare amchart: XYChart;
    dateAxis: DateAxis;
    valueAxis: ValueAxis;
    scale: timeScalesEnum;
    tooltipHovered: boolean = true;
    tooltipDisplayInterval: NodeJS.Timeout;
    tooltipHoverIndex: number;

    readonly singleSeries: boolean = false;
    readonly hoursOf31Days = 744;
    readonly hoursOf30Weeks = 5040;

    constructor(
        series: SeriesModel[],
        options: DateAndValueXYChartOptionsModel = {},
        metadata?: ChartMetadataModel,
    ) {
        super(series, options, metadata);
    }

    public initChart(element: HTMLElement): void {
        super.initChart(element, XYChart);
        this.amchart.paddingRight = 30;
        this.amchart.paddingBottom = 20;
        if (!!this.header && !!this.options.styles.headerMarginBottom) {
            this.header.marginBottom = this.options.styles.headerMarginBottom;
        }

        this.amchart.arrangeTooltips = true;

        this.dateAxis = this.amchart.xAxes.push(new DateAxis());
        this.dateAxis.cursorTooltipEnabled = false;
        this.dateAxis.renderer.grid.template.disabled = true;
        this.dateAxis.renderer.line.stroke = am4core.color('#ced4da');
        this.dateAxis.renderer.line.strokeOpacity = 1;
        this.dateAxis.renderer.line.strokeWidth = 2;
        this.dateAxis.renderer.ticks.template.disabled = false;
        this.dateAxis.renderer.ticks.template.stroke = am4core.color('#ced4da');
        this.dateAxis.renderer.ticks.template.strokeOpacity = 1;
        this.dateAxis.renderer.ticks.template.length = 6;
        this.dateAxis.renderer.ticks.template.strokeWidth = 1;
        this.dateAxis.renderer.minGridDistance = 60;
        this.dateAxis.startLocation = 0.5;
        this.dateAxis.endLocation = 0.6;
        this.dateAxis.zoomable = false;
        this.dateAxis.gridIntervals.setAll([
            { timeUnit: 'minute', count: 5 },
            { timeUnit: 'minute', count: 15 },
            { timeUnit: 'minute', count: 30 },
            { timeUnit: 'hour', count: 1 },
            { timeUnit: 'hour', count: 2 },
            { timeUnit: 'hour', count: 4 },
            { timeUnit: 'day', count: 1 },
            { timeUnit: 'day', count: 2 },
            { timeUnit: 'day', count: 7 },
            { timeUnit: 'month', count: 1 },
            { timeUnit: 'month', count: 3 },
            { timeUnit: 'year', count: 1 }
        ]);

        this.valueAxis = this.amchart.yAxes.push(new ValueAxis());
        this.valueAxis.numberFormatter = new am4core.NumberFormatter();
        this.valueAxis.numberFormatter.numberFormat = '#a';
        this.valueAxis.renderer.grid.template.disabled = true;
        this.valueAxis.renderer.line.stroke = am4core.color('#ced4da');
        this.valueAxis.renderer.line.strokeOpacity = 1;
        this.valueAxis.renderer.line.strokeWidth = 2;
        this.valueAxis.cursorTooltipEnabled = false;
        this.valueAxis.calculateTotals = true;
        // Makes it so the value axis can't show decimal places, just whole numbers
        this.valueAxis.maxPrecision = 0;

        (this.options as DateAndValueXYChartOptionsModel).seriesDefinitions.forEach((series, index) => {
            if (!series.key) {
                console.error('Please provide a key for each series');
            }
            this.addSeries(series.key, series.label, index);
        });

        if (this.options.legendContainerElement ) {
            this.initCustomLegend();
        } else {
            // Legend
            this.amchart.legend = new Legend();
            this.amchart.legend.itemContainers.template.togglable = false;
            this.amchart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
            this.amchart.legend.reverseOrder = true;
            this.amchart.legend.position = 'bottom';
            this.amchart.legend.align = 'center';
            this.amchart.legend.contentAlign = 'center';
            this.amchart.legend.fontSize = '0.875rem';
            this.amchart.legend.marginTop = 10;
            this.amchart.legend.scrollable = true;

            this.amchart.legend.labels.template.text = '{name}';
            this.amchart.legend.labels.template.truncate = false;
            this.amchart.legend.markers.template.scale = 0.75;

            this.amchart.legend.itemContainers.template.width = 100;
            this.amchart.legend.itemContainers.template.paddingTop = 2;
            this.amchart.legend.itemContainers.template.paddingBottom = 0;
            this.amchart.legend.itemContainers.template.paddingLeft = 12;
        }

        // Cursor
        this.amchart.cursor = new XYCursor();
        this.amchart.cursor.maxTooltipDistance = 0;
        this.amchart.cursor.lineY.disabled = true;
        this.amchart.cursor.lineX.stroke = am4core.color('#ffffff');
        this.amchart.cursor.lineX.strokeWidth = 1;
        this.amchart.cursor.lineX.strokeOpacity = 1;

        const smallChartWidth = ChartSizeHelper.getMinWidthAtMinCanvas();
        const smallChartHeight = ChartSizeHelper.getCellHeight();
        const largeChartWidth = ChartSizeHelper.getWidthAtMinCanvas(4, 2);
        const largeChartHeight = ChartSizeHelper.getHeight(3, 2);
        this.amchart.events.on('sizechanged', event => {
            const chart = event.target;
            if (chart.pixelWidth <= smallChartWidth || chart.pixelHeight <= smallChartHeight) {
                this.dateAxis.fontSize = '0.75rem';
                this.valueAxis.fontSize = '0.75rem';
                if (this.header) {
                    this.header.marginBottom = 5;
                }
                if (this.amchart.legend) {
                    this.amchart.legend.fontSize = '0.75rem';
                    this.amchart.legend.marginTop = 0;
                    this.amchart.legend.maxHeight = 40;

                }
            } else {
                this.dateAxis.fontSize = '0.875rem';
                this.valueAxis.fontSize = '0.875rem';
                if (this.header) {
                    this.header.marginBottom = 10;
                }
                if (this.amchart.legend) {
                    this.amchart.legend.fontSize = '0.875rem';
                    this.amchart.legend.marginTop = 10;
                    this.amchart.legend.maxHeight = 500;

                }
            }

            if (chart.pixelWidth > largeChartWidth && chart.pixelHeight > largeChartHeight) {
                this.amchart.legend.markers.template.scale = 1;
            } else {
                this.amchart.legend.markers.template.scale = 0.75;

            }
        });

        // Responsive
        if (this.options.responsive) {
            this.amchart.responsive.enabled = true;
            this.amchart.svgContainer.autoResize = true;
            this.amchart.svgContainer.htmlElement.style.maxHeight = '500px';
            this.amchart.svgContainer.htmlElement.style.minHeight = '300px';

            // Large Screen
            this.amchart.responsive.rules.push(
                {
                    id: 'large',
                    relevant: (target) => target.pixelWidth > 768,
                    state: function (target, stateId) {
                        if (target instanceof AxisRendererX) {
                            const xAxisState = target.labels.template.states.create(stateId);
                            xAxisState.properties.fontSize = 16;
                            return xAxisState;
                        }

                        if (target instanceof AxisRendererY) {
                            const yAxisState = target.labels.template.states.create(stateId);
                            yAxisState.properties.fontSize = 16;
                            return yAxisState;
                        }

                        if (target instanceof ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.fontSize = 16;
                            return valueAxisState;
                        }

                        if (target instanceof Cursor) {
                            const cursorState = target.states.create(stateId);
                            cursorState.properties.fontSize = 16;
                            return cursorState;
                        }

                        if (target instanceof Series) {
                            if (target.tooltip) {
                                const seriesTooltipState = target.tooltip.states.create(stateId);
                                seriesTooltipState.properties.fontSize = 16;
                                return seriesTooltipState;
                            }
                        }
                    }
                }
            );

            // Small screen
            this.amchart.responsive.rules.push(
                {
                    id: 'small',
                    relevant: (target) => {
                        return target.pixelWidth <= 768;
                    },
                    state: function (target, stateId) {
                        if (target instanceof AxisRendererX) {
                            const xAxisState = target.labels.template.states.create(stateId);
                            xAxisState.properties.fontSize = 14;
                            return xAxisState;
                        }

                        if (target instanceof AxisRendererY) {
                            const yAxisState = target.labels.template.states.create(stateId);
                            yAxisState.properties.fontSize = 14;
                            return yAxisState;
                        }

                        if (target instanceof ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.fontSize = 14;
                            return valueAxisState;
                        }

                        if (target instanceof Cursor) {
                            const cursorState = target.states.create(stateId);
                            cursorState.properties.fontSize = 14;
                            return cursorState;
                        }

                        if (target instanceof Series) {
                            if (target.tooltip) {
                                const seriesTooltipState = target.tooltip.states.create(stateId);
                                seriesTooltipState.properties.fontSize = 14;
                                return seriesTooltipState;
                            }
                        }
                    }
                }
            );
        }
        // keyboard accessibility
        this.handleStackedRiverChartKeyboardInteraction();

        if ((this.options as DateAndValueXYChartOptionsModel).verticalLines?.length > 0) {
            (this.options as DateAndValueXYChartOptionsModel).verticalLines.forEach((lineDef) => {
                this.addAxisLine(this.dateAxis, lineDef, true);
            });
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        this.amchart.series.each((series) => {
            if (!series) {
                return;
            }
            const item: CustomLegendItem = {
                label: series.name,
                color: series.stroke.toString(),
                // TODO: this works, but we want to disable the item in the legend, which isn't available yet
                // click: () => {
                //     series.visible = !series.visible;
                // }
            };
            items.push(item);
        });

        return items;
    }

    public addSeries(key: string, label: string, idx: number): void {
        const amseries = this.amchart.series.push(new LineSeries());
        // stacked river chart uses openValueY for the bottom boundary and valueY for top boundary
        amseries.dataFields.valueY = key + '_hi';
        amseries.dataFields.openValueY = key + '_lo';
        amseries.dataFields.dateX = 'time';
        amseries.tooltip.tooltipY = am4core.percent(20);

        const color = this.styles.colorMap[key];
        amseries.fill = am4core.color(color);
        amseries.name = label;
        amseries.strokeWidth = 1;
        amseries.stroke = am4core.color(color);
        amseries.fillOpacity = 0.9;
        amseries.tensionX = 0.78;
        amseries.tooltip.animationDuration = 400;
        amseries.cursorTooltipEnabled = false;
        amseries.tooltip.showTooltipOn = 'always';

        const bullet: CircleBullet = amseries.bullets.push(new CircleBullet());
        bullet.tooltipText = '[bold font-size: 1rem]{name}[/]: {' + key + '} ({' + key + 'Percent}%)';
        bullet.circle.strokeWidth = 1;
        bullet.circle.strokeOpacity = 0;
        bullet.circle.radius = this.options.styles?.bulletRadius || 20;
        bullet.circle.fill = am4core.color(color);
        bullet.circle.fillOpacity = 0;
        bullet.states.create('hover');
        bullet.readerTitle = '{name}, {' + key + '}, {' + key + 'Percent}%';
        bullet.propertyFields.id = '' + idx || ''; // @HACK for keyboard navigation a11y, see handleStackedRiverChartKeyboardInteraction()
        bullet.propertyFields['key'] = key;
        if (idx === 0){
            amseries.tooltip.pointerOrientation = 'up';
            amseries.tooltip.background.pointerLength = 20;
            amseries.tooltip.background.pointerBaseWidth = 20;
        } else if (idx === this.series.length - 1) {
            amseries.tooltip.pointerOrientation = 'down';
            amseries.tooltip.background.pointerLength = 20;
            amseries.tooltip.background.pointerBaseWidth = 20;
        } else {
            amseries.tooltip.pointerOrientation = 'horizontal';
        }
    }

    public updateData(data: any[]) {
        this.rawData = data;
        if (!data || data.some(series => series === null)) {
            this.amchart.data = null;

            return;
        }

        const seriesIds = [];
        const seriesLabels = [];
        (this.options as DateAndValueXYChartOptionsModel).seriesDefinitions.forEach(series => {
            seriesIds.push(series.key);
            seriesLabels.push(series.label);
        });

        const flatData = this.groupData(data[0], seriesIds);
        this.amchart.data = (JSON.parse(JSON.stringify(flatData)));
        this.updateSeriesStyles();


        // Need to reverse the series array
        // in order to make serie values in tooltip appear in the same order as the legend (top the bottom)
        const reverseSeries =  this.amchart.series.values.slice().reverse();
        const reverseSeriesIds =  seriesIds.slice().reverse();
        const reverseSeriesLabels =  seriesLabels.slice().reverse();
        // Loop through each bullets of each series to add the tooltip html
        if ((this.options as DateAndValueXYChartOptionsModel).tooltipHTML) {
            this.amchart.series.values.forEach(series => {
                series.bullets.values.forEach(bullet => {
                    bullet.adapter.add('tooltipHTML', (text, target) => {
                        let tooltip =
                        `<div
                            style="margin: 10px; font-size: 0.875rem;"
                        >
                            <div
                                style="border-bottom: 1px solid #ced4da; padding-bottom: 5px"
                            >
                                <b style="font-weight: 600">{dateX}</b>
                            </div>
                        `;
                        reverseSeries.forEach((item, index) => {
                            tooltip += (this.options as DateAndValueXYChartOptionsModel).tooltipHTML
                                .replace('{color}', this.styles.colorMap[reverseSeriesIds[index]])
                                .replace('{legendLabel}', reverseSeriesLabels[index])
                                .replace(/key/g, reverseSeriesIds[index])
                                .replace('{viewMentionUrl}', target.dataItem.dataContext[reverseSeriesIds[index] + '_viewMentionUrl']);

                        });
                        tooltip += '</div>';
                        return tooltip;
                    });
                });
                // set tooltip style
                series.tooltip.getFillFromObject = false;
                series.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                series.tooltip.background.stroke = am4core.color('#ced4da');
                series.tooltip.background.cornerRadius = 8;
                series.tooltip.background.fillOpacity = 1;
                // clear tooltip background shadow
                series.tooltip.background.filters.clear();
                series.tooltip.label.padding(0, 0, 0, 0);
                series.tooltip.label.fill = am4core.color(chartColors.agprBlack);
                series.tooltip.label.interactionsEnabled = true;
                series.tooltip.keepTargetHover = true;
            });
        }

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.valueAxis.disabled = true;
            this.dateAxis.disabled = true;
            if (this.amseries) {
                this.amseries.disabled = true;
            }
            this.showNoContentMessage();
        }

        this.ready.next(true);
    }

    /**
     * Required but not currently used
     */
    public updateSeriesStyles(): void {}

    /**
      * Exports data using amcharts
      */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {label: 'label'};

        for (const [key, value] of (this.options as DateAndValueXYChartOptionsModel).exportLabelDefinitions.entries()) {
            dataFields[key] = value;
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return this.amchart.data.length === 0;
    }

    public clone(): StackedRiverChartModel {
        return new StackedRiverChartModel(
            this.series.map(s => s.clone()),
            this.options,
            this.metadata
        );
    }

    /**
     * Modify bullet DOM so that each bullet can be navigated by arrow left and right
     * Using TAB key will move focus between lines
     * Hit Enter on bullet point will open its mention link
     */
    handleStackedRiverChartKeyboardInteraction() {
        // 'ready' event doesn't always fire, so set timeout here instead
        setTimeout(() => {
            const chartBullets = [];
            this.amchart?.series?.values.forEach((series, index) => {
                series.bullets.values.forEach(bullet => {
                    // @HACK - when there's legend, the first value is for its legend bullet instead of the first bullet,
                    // have to remove it so arrow key navigation can work properly
                    if (this.amchart.legend) {
                        bullet.clones.values.splice(0,1);
                    }
                    chartBullets.push(...bullet.clones.values);
                });

            });
            for (let i = 0; i < chartBullets.length; i++) {
                const circleBullet = chartBullets[i];
                circleBullet.hoverOnFocus = true;
                circleBullet.dom.addEventListener('keydown', (e) => {
                    switch (e.key) {
                        case ActionKeys.RIGHT:
                            if (chartBullets[i + 1]?.propertyFields.id === circleBullet.propertyFields.id) {
                                e.preventDefault();
                                chartBullets[i + 1].dom.focus();
                            }
                            break;

                        case ActionKeys.LEFT:
                            if (chartBullets[i - 1]?.propertyFields.id === circleBullet.propertyFields.id) {
                                e.preventDefault();
                                chartBullets[i - 1].dom.focus();
                            }
                            break;

                        case ActionKeys.TAB:
                            let seriesIndex = +circleBullet.propertyFields.id ? +circleBullet.propertyFields.id : 0;
                            if (e.shiftKey) {
                                seriesIndex -= 1;
                            } else {
                                seriesIndex += 1;
                            }
                            const nextSeriesBullet = chartBullets.find(x =>
                                +x.propertyFields.id === seriesIndex
                            );
                            if (nextSeriesBullet) {
                                e.preventDefault();
                                nextSeriesBullet.dom.focus();
                            } else if (seriesIndex >= this.series.length) {
                                e.preventDefault();
                                if (this.amchart.legend) {
                                    this.amchart.legend.dom.tabIndex = 0;
                                    this.amchart.legend.dom.focus();
                                }
                            }
                            break;
                        case ActionKeys.ENTER:
                            circleBullet.events.on('hit', function(ev) {
                                const seriesKey =  ev.target.propertyFields['key'];
                                const viewMentionLink = ev.target.tooltipDataItem.dataContext[`${seriesKey}_viewMentionUrl`];
                                if (viewMentionLink) {
                                    (document.querySelector(`[class="viewMentionLink"][href*='${viewMentionLink}']`) as HTMLElement)?.click();
                                }
                            });
                            break;
                    }
                });
            }
        }, 5000);
    }

    // It relies on all data points on the same date to calculate the total, and each boundary point
    groupData(data: any[], seriesIds: string[]): any[] {
        let timeInterval = (this.options as DateAndValueXYChartOptionsModel).timeInterval || ChartTimeIntervalsEnum.DYNAMIC;
        const chartTimeIntervalsToIsNewIntervalMethodMap: Map<ChartTimeIntervalsEnum, (a: Moment, b: Moment) => boolean> = new Map([
            [ ChartTimeIntervalsEnum.DYNAMIC, (a: Moment, b: Moment) => true ],
            [ ChartTimeIntervalsEnum.WEEK, (a: Moment, b: Moment) => a.week() !== b.week() ],
            [ ChartTimeIntervalsEnum.MONTH, (a: Moment, b: Moment) => a.month() !== b.month() ],
            [ ChartTimeIntervalsEnum.QUARTER, (a: Moment, b: Moment) => a.quarter() !== b.quarter() ],
            [ ChartTimeIntervalsEnum.YEAR, (a: Moment, b: Moment) => a.year() !== b.year() ]
        ]);
        if (data?.length && (timeInterval === ChartTimeIntervalsEnum.DYNAMIC)) {
            const duration = moment(data[data.length - 1]['xValue']).diff(data[0]['xValue'], 'hours');
            if (duration > this.hoursOf31Days) {
                timeInterval = ChartTimeIntervalsEnum.WEEK;
            }
            if (duration > this.hoursOf30Weeks) {
                timeInterval = ChartTimeIntervalsEnum.MONTH;
            }
        }
        const needsNewDataPoint = chartTimeIntervalsToIsNewIntervalMethodMap.get(timeInterval);

        const flatData = [];
        let dataPoint = {};
        let startTime: number, currentTime: number, newTime: number;
        for (let i = 0; i < data.length; i++) {
            if (i === 0) {
                startTime = currentTime = data[i].xValue;
            }
            dataPoint['time'] = startTime;
            dataPoint['label'] = moment(startTime).format('MMM DD, YYYY HH:mm');
            // add current date value for grouping
            for (let j = 0; j < seriesIds.length; j++) {
                if (!data[i]) {
                    // If there's no data for that series/node, just skip it
                    continue;
                }
                const key = seriesIds[j];
                const value = data[i][`yValue_${key}`];
                dataPoint[key] = (dataPoint[key] || 0) + value;
            }
            newTime = data[i + 1]?.xValue;
            // if current date and next date are not in the same week, quarter, etc. we will clean up and push current data point
            if (!newTime || needsNewDataPoint(moment(currentTime), moment(newTime))) {
                let sum = 0;
                for (let j = 0; j < seriesIds.length; j++) {
                    if (!data[i]) {
                        // If there's no data for that series/node, just skip it
                        continue;
                    }
                    const key = seriesIds[j];
                    const value = dataPoint[key];
                    dataPoint[key + '_lo'] = sum;
                    dataPoint[key + '_hi'] = sum + value;
                    sum += value;
                }
                for (let j = 0; j < seriesIds.length; j++) {
                    const percent = dataPoint[seriesIds[j]] / sum * 100;
                    dataPoint[seriesIds[j] + 'Percent'] = (!isNaN(percent) ? percent : 0).toFixed(2);
                    if (data[i][`mentionFilters_${seriesIds[j]}`]) {
                        const mentionFilters = new MentionSearchCriteriaModel(JSON.parse(data[i][`mentionFilters_${seriesIds[j]}`]));
                        mentionFilters.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                        mentionFilters.query.dateQuery.absoluteRange = {
                            start: new Date(startTime),
                            end: new Date(newTime || moment(currentTime).add(1, 'day').valueOf())
                        };
                        try {
                            dataPoint[seriesIds[j] + '_viewMentionUrl'] = (this.series[0] as ReportSeriesModel).getMentionSeriesUrl(mentionFilters);
                        } catch {
                            dataPoint[seriesIds[j] + '_viewMentionUrl'] = '';
                        }
                    } else if (data[i][`viewMentionUrl_${seriesIds[j]}`])  {
                        dataPoint[seriesIds[j] + '_viewMentionUrl'] = data[i][`viewMentionUrl_${seriesIds[j]}`];
                    } else {
                        dataPoint[seriesIds[j] + '_viewMentionUrl'] = '';
                    }
                }
                if (startTime !== currentTime) {
                    dataPoint['label'] += ' - ' + moment(currentTime).add(1, 'day').format('MMM DD, YYYY HH:mm');
                }
                flatData.push(dataPoint);

                dataPoint = {};
                startTime = currentTime = newTime;
                dataPoint['time'] = startTime;
                dataPoint['label'] = moment(startTime).format('MMM DD, YYYY HH:mm');
            }
            currentTime = newTime;
        }


        return flatData;
    }
}
